/**
 * Hash-based routing under the configurable base path (requirement 5: "one
 * screen per organism ... a family screen and a fleet screen, routing by
 * hash/path under the base path"). Hash routing needs no server rewrite
 * rule, which matters for a static sub-path mount (§B.6/requirement 1: "no
 * server code").
 */

export type Route =
  | { readonly kind: "organism"; readonly role: string }
  | { readonly kind: "family" }
  | { readonly kind: "benchmarks" }
  | { readonly kind: "fleet" };

const DEFAULT_ROUTE: Route = { kind: "fleet" };

export function parseHash(hash: string): Route {
  const trimmed = hash.replace(/^#\/?/, "");
  if (trimmed === "" || trimmed === "fleet") return DEFAULT_ROUTE;
  if (trimmed === "family") return { kind: "family" };
  if (trimmed === "benchmarks") return { kind: "benchmarks" };
  const organismMatch = /^organism\/([a-zA-Z0-9_-]+)$/.exec(trimmed);
  if (organismMatch?.[1]) return { kind: "organism", role: organismMatch[1] };
  return DEFAULT_ROUTE;
}

export function routeToHash(route: Route): string {
  switch (route.kind) {
    case "organism":
      return `#/organism/${route.role}`;
    case "family":
      return "#/family";
    case "benchmarks":
      return "#/benchmarks";
    case "fleet":
      return "#/fleet";
  }
}

export class Router {
  private listeners = new Set<(route: Route) => void>();

  constructor() {
    window.addEventListener("hashchange", this.handleHashChange);
  }

  private handleHashChange = (): void => {
    const route = parseHash(window.location.hash);
    for (const listener of this.listeners) listener(route);
  };

  get current(): Route {
    return parseHash(window.location.hash);
  }

  navigate(route: Route): void {
    window.location.hash = routeToHash(route);
  }

  subscribe(listener: (route: Route) => void): () => void {
    this.listeners.add(listener);
    listener(this.current);
    return () => this.listeners.delete(listener);
  }

  dispose(): void {
    window.removeEventListener("hashchange", this.handleHashChange);
    this.listeners.clear();
  }
}
